/* eslint-disable max-lines */
import React, {useCallback, useMemo, useEffect, useRef} from "react";
import {gql} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {
  Avatar,
  Competence,
  CutWordsDictionary,
  HotWordsDictionary,
  Challenge,
  ChallengeMode,
  ChallengeBadWordsFilter,
  ChallengeDisplayMode,
  ChallengeInput,
  ChallengeInputMode,
  ChallengeRecognitionAccuracy,
  ParametricResultDescription,
  ParametricResultDescriptionInput,
  ResultDescription,
  ResultDescriptionInput,
  MultipleChoiceScoringOption,
  TriggerDictionaryType,
} from "../../schema";
import {useFormState} from "../../hooks/useFormState";
import {useGuide} from "../../providers/guide";
import {classes as tourClasses} from "../../scenes/editor/challenge-edit.tour";

import AvatarSelect from "./AvatarSelect";
import Select from "../../ui/select";
import WithTooltip from "../../ui/tooltip";
import Checkbox from "../../ui/checkbox";
import FileInput from "./FileInput";
import {HelpCircleIcon} from "../../ui/icons";
import Link from "../../ui/link";

import cn from "classnames";
import classes from "./ChallengeCustomizeForm.module.css";
import {useModal} from "../ModalProvider";
import EditableTextarea from "./EditableTextarea";
import {
  useAddParametricResultDescriptionMutation,
  useAddResultDescriptionMutation,
  useDeleteParametricResultDescriptionMutation,
  useDeleteResultDescriptionMutation,
  useUpdateParametricResultDescriptionMutation,
  useUpdateResultDescriptionMutation,
} from "../../scenes/editor/challenge-edit.graphql";
import {getLanguages} from "../../i18n";
import {YANDEX_TTS_PHRASE_MAX_LENGTH} from "../../settings";
import useVisibility from "../../hooks/useVisibility";
import {analyticsSendEvent} from "../../libs/analytics";
import {useCurrentUser} from "../../App.context";
import ResultDescriptionModal from "./ResultDescriptionModal";
import Input from "../../ui/input";
import ChallengeDictionaryForm from "./ChallengeDictionaryForm";
import ParametricResultDescriptionModal from "./ParametricResultDescriptionModal";
import DisplayResultModeSelector from "./challenge-edit/challenge-settings/DisplayResultModeSelector";
import CompetenciesCreatableSelect from "./CompetenciesCreatableSelect";
import {ResultDescriptionsList} from "./ResultDescriptionsList";
import {toast} from "react-toastify";
import CustomParametersEditor from "./CustomParametersEditor";

type props = {
  className?: string;
  challenge: Challenge;
  competencies?: Competence[];
  triggerDicts: TriggerDictionaryType[] | undefined;
  cutWordsDicts: CutWordsDictionary[] | undefined;
  hotWordsDicts: HotWordsDictionary[] | undefined;
  resultDescriptions: ResultDescription[] | undefined;
  parametricResultDescriptions: ParametricResultDescription[] | undefined;
  avatars: Avatar[];
  supportedVoices?: string[];
  disabled?: boolean;
  onSave?: (data: ChallengeInput) => void;
  competenciesFromActionIds?: string[];
};

type formState = {
  avatarId: NonNullable<ChallengeInput["avatarId"]>;
  languageCode: NonNullable<ChallengeInput["languageCode"]>;
  mode: NonNullable<ChallengeInput["mode"]>;
  showResultsForEachParameter?: NonNullable<ChallengeInput["showResultsForEachParameter"]> | "on";
  background?: ChallengeInput["background"];
  displayMode: ChallengeInput["displayMode"];
  inputMode: NonNullable<ChallengeInput["inputMode"]>;
  badWordsFilter: ChallengeInput["badWordsFilter"];
  generateVideo?: NonNullable<ChallengeInput["generateVideo"]> | "on";
  showDetailedResults?: NonNullable<ChallengeInput["showDetailedResults"]> | "on";
  evaluateScenarios?: NonNullable<ChallengeInput["evaluateScenarios"]> | "on";
  selectableInputs?: NonNullable<ChallengeInput["selectableInputs"]> | "on";
  alwaysCanSkipSteps?: NonNullable<ChallengeInput["alwaysCanSkipSteps"]> | "on";
  ignoreFailedInputs?: NonNullable<ChallengeInput["ignoreFailedInputs"]> | "on";
  useCharacterVoiceForErrors?: NonNullable<ChallengeInput["useCharacterVoiceForErrors"]> | "on";
  muteNonUserSpeech?: NonNullable<ChallengeInput["muteNonUserSpeech"]> | "on";
  showPreviousPhrase?: NonNullable<ChallengeInput["showPreviousPhrase"]> | "on";
  recognitionAccuracy: NonNullable<ChallengeInput["recognitionAccuracy"]>;
  startMessage?: NonNullable<ChallengeInput["startMessage"]>;
  resultMessage?: NonNullable<ChallengeInput["resultMessage"]>;
  attemptsLimit?: NonNullable<ChallengeInput["attemptsLimit"]>;
  timeLimit?: NonNullable<ChallengeInput["timeLimit"]>;
  actionTimeLimit?: NonNullable<ChallengeInput["actionTimeLimit"]>;
  inputPhraseMaxLength?: NonNullable<ChallengeInput["inputPhraseMaxLength"]>;
  avatarPhraseMaxLength?: NonNullable<ChallengeInput["avatarPhraseMaxLength"]>;
  systemPhraseMaxLength?: NonNullable<ChallengeInput["systemPhraseMaxLength"]>;
  customParameterIds: NonNullable<ChallengeInput["customParameterIds"]>;
  displayParametricResultMode: NonNullable<ChallengeInput["displayParametricResultMode"]>;
  multipleChoiceScoringOption: NonNullable<ChallengeInput["multipleChoiceScoringOption"]>;
  avatarVoiceId?: NonNullable<ChallengeInput["avatarVoiceId"]>;
  systemVoiceId?: NonNullable<ChallengeInput["systemVoiceId"]>;
  competenceIds?: ChallengeInput["competenceIds"];
  isAllResponsesEnabled?: NonNullable<ChallengeInput["isAllResponsesEnabled"]> | "on";
  isSessionResultFeedbackEnabled?: NonNullable<ChallengeInput["isSessionResultFeedbackEnabled"]> | "on";
  instructionFile?: ChallengeInput["instructionFile"];
  isUserStart?: NonNullable<ChallengeInput["isAgentStart"]> | "on";
};

export default function ChallengeCustomizeForm({
  className,
  challenge,
  competencies,
  triggerDicts,
  cutWordsDicts,
  hotWordsDicts,
  resultDescriptions,
  parametricResultDescriptions,
  avatars,
  supportedVoices,
  disabled,
  onSave,
  competenciesFromActionIds,
}: props) {
  const {t} = useTranslation();

  const user = useCurrentUser();
  const inExperimentalMode = user?.experimentalMode;
  const isStaff = user?.isStaff;

  const avatarsEx = useMemo(() => {
    let avatarsEx = [...avatars];

    if (!avatarsEx.find((avatar) => avatar.id === challenge.avatar.id)) {
      avatarsEx.unshift(challenge.avatar);
    }

    return avatarsEx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge.avatar.id, challenge.mode, ...avatars.map((a) => a.id)]);

  const lastUploadedFiles = useRef<{instructionFile?: File | null; background?: File | null}>({});

  const formState = useFormState<formState>({
    initialValues: {
      avatarId: challenge.avatar.id,
      languageCode: challenge.languageCode,
      mode: challenge.mode,
      showResultsForEachParameter: challenge.showResultsForEachParameter ? "on" : undefined,
      customParameterIds: challenge.customParameters?.map((p) => p.id) ?? [],
      displayParametricResultMode: challenge.displayParametricResultMode,
      displayMode: challenge.displayMode,
      inputMode: challenge.inputMode,
      badWordsFilter: challenge.badWordsFilter,
      generateVideo: challenge.generateVideo ? "on" : undefined,
      showDetailedResults: challenge.showDetailedResults ? "on" : undefined,
      evaluateScenarios: challenge.evaluateScenarios ? "on" : undefined,
      selectableInputs: challenge.selectableInputs ? "on" : undefined,
      alwaysCanSkipSteps: challenge.alwaysCanSkipSteps ? "on" : undefined,
      ignoreFailedInputs: challenge.ignoreFailedInputs ? "on" : undefined,
      useCharacterVoiceForErrors: challenge.useCharacterVoiceForErrors ? "on" : undefined,
      muteNonUserSpeech: challenge.muteNonUserSpeech ? "on" : undefined,
      showPreviousPhrase: challenge.showPreviousPhrase ? "on" : undefined,
      recognitionAccuracy: challenge.recognitionAccuracy,
      startMessage: challenge.startMessage ?? undefined,
      resultMessage: challenge.resultMessage ?? undefined,
      attemptsLimit: challenge.attemptsLimit ?? 0,
      timeLimit: challenge.timeLimit ?? 0,
      actionTimeLimit: challenge.actionTimeLimit ?? 0,
      inputPhraseMaxLength: challenge.inputPhraseMaxLength ?? 140,
      avatarPhraseMaxLength: challenge.avatarPhraseMaxLength ?? 140,
      systemPhraseMaxLength: challenge.systemPhraseMaxLength ?? 140,
      avatarVoiceId: challenge.avatarVoiceId ?? undefined,
      systemVoiceId: challenge.systemVoiceId ?? undefined,
      multipleChoiceScoringOption:
        challenge.multipleChoiceScoringOption ?? MultipleChoiceScoringOption.COMPLETELY_CORRECT,
      competenceIds: challenge.competences?.map((c) => c.id) ?? [],
      isAllResponsesEnabled: !challenge.isAllResponsesEnabled ? "on" : undefined,
      isSessionResultFeedbackEnabled: challenge.isSessionResultFeedbackEnabled ? "on" : undefined,
      instructionFile: challenge.instructionFile ?? undefined,
      isUserStart: !challenge.isAgentStart ? "on" : undefined,
    },
    preventDefault: true,
    onChange: (data) => {
      const onSaveData: ChallengeInput = {
        ...data,
        avatarId: data.avatarId ? data.avatarId : "none",
        generateVideo: data.generateVideo === "on",
        showDetailedResults: data.showDetailedResults === "on",
        evaluateScenarios: data.evaluateScenarios === "on",
        selectableInputs: data.selectableInputs === "on",
        alwaysCanSkipSteps: data.alwaysCanSkipSteps === "on",
        ignoreFailedInputs: data.ignoreFailedInputs === "on",
        showResultsForEachParameter: data.showResultsForEachParameter === "on",
        useCharacterVoiceForErrors: data.useCharacterVoiceForErrors === "on",
        muteNonUserSpeech: data.muteNonUserSpeech === "on",
        showPreviousPhrase: data.showPreviousPhrase === "on",
        background: data.background === "" ? null : data.background,
        displayMode: data.displayMode === undefined ? ChallengeDisplayMode.DEFAULT : data.displayMode,
        inputMode: data.inputMode === undefined ? ChallengeInputMode.VOICE : data.inputMode,
        isAllResponsesEnabled: data.isAllResponsesEnabled !== "on",
        isSessionResultFeedbackEnabled: data.isSessionResultFeedbackEnabled === "on",
        instructionFile: data.instructionFile === "" ? null : data.instructionFile,
        isAgentStart: data.isUserStart !== "on",
      };

      if (onSaveData.instructionFile instanceof File) {
        if (lastUploadedFiles.current.instructionFile === onSaveData.instructionFile) {
          delete onSaveData.instructionFile;
        } else {
          lastUploadedFiles.current.instructionFile = onSaveData.instructionFile;
        }
      } else if (typeof onSaveData.instructionFile === "string") {
        delete onSaveData.instructionFile;
      }

      if (onSaveData.background instanceof File) {
        if (lastUploadedFiles.current.background === onSaveData.background) {
          delete onSaveData.background;
        } else {
          lastUploadedFiles.current.background = onSaveData.background;
        }
      } else if (typeof onSaveData.background === "string") {
        delete onSaveData.background;
      }

      if (!isStaff) {
        delete onSaveData.avatarVoiceId;
        delete onSaveData.systemVoiceId;
      }

      onSave && onSave(onSaveData);
    },
  });

  const currentCompetences = useMemo(() => {
    return formState.values.competenceIds ?? [];
  }, [formState.values.competenceIds]);

  const competenciesFromActions = useMemo(() => {
    if (!competenciesFromActionIds || competenciesFromActionIds.length === 0) {
      return [];
    }
    return (
      competencies
        ?.filter((c) => competenciesFromActionIds.includes(c.id))
        .filter((c) => !currentCompetences.includes(c.id)) ?? []
    );
  }, [competenciesFromActionIds, competencies, currentCompetences]);

  useEffect(() => {
    if (
      formState.values.isSessionResultFeedbackEnabled === "on" &&
      currentCompetences.length === 0 &&
      competenciesFromActions.length === 0
    ) {
      formState.setValues((prev) => ({
        ...prev,
        isSessionResultFeedbackEnabled: undefined,
      }));
    }
  }, [formState, currentCompetences, competenciesFromActions]);

  const challengeModeHelpText = useMemo(
    () =>
      [
        "<p>" + t("components.ChallengeCustomizeForm.challengeModeHelpText.COMPETENCE_TEST") + "</p>",
        "<p>" + t("components.ChallengeCustomizeForm.challengeModeHelpText.CHOICE_BASED_STUDY") + "</p>",
        "<P>" + t("components.ChallengeCustomizeForm.challengeModeHelpText.CHOICES_WITH_CUSTOM_SCORE") + "</p>",
        "<P>" + t("components.ChallengeCustomizeForm.challengeModeHelpText.CUSTOM_PARAMETERS_TEST") + "</p>",
        "<p>" + t("components.ChallengeCustomizeForm.challengeModeHelpText.RPG_MODE") + "</p>",
      ].join("\n"),
    [t],
  );

  const imageSrc =
    (!!formState.values.background || challenge.background) && formState.values.background !== ""
      ? (formState.values.background ?? challenge.background ?? undefined)
      : undefined;

  const instructionFileSrc: string | undefined = useMemo(
    () =>
      !!formState.values.instructionFile || !!challenge.instructionFile
        ? typeof formState.values.instructionFile === "string" && formState.values.instructionFile !== ""
          ? formState.values.instructionFile
          : formState.values.instructionFile instanceof File
            ? URL.createObjectURL(formState.values.instructionFile)
            : (challenge.instructionFile ?? undefined)
        : undefined,
    [challenge.instructionFile, formState.values.instructionFile],
  );

  const instructionFileName = useMemo(() => {
    if (formState.values.instructionFile === "") {
      return undefined;
    }
    if (formState.values.instructionFile instanceof File) {
      return formState.values.instructionFile.name;
    }
    return challenge.instructionFileName ?? undefined;
  }, [challenge.instructionFileName, formState.values.instructionFile]);

  const {open: openGuide} = useGuide();

  const onChallengeTypeHelpClick = useCallback(() => {
    openGuide("editor.challenge.modes");
  }, [openGuide]);

  const {add: addModal} = useModal();

  const [applyAddResultDescriptionMutation] = useAddResultDescriptionMutation();
  const [applyUpdateResultDescriptionMutation] = useUpdateResultDescriptionMutation();
  const [applyDeleteResultDescriptionMutation] = useDeleteResultDescriptionMutation();

  const onResultDescAdd = useCallback(
    (data: ResultDescriptionInput) => {
      const challengeId = challenge.id;
      analyticsSendEvent("editorChallengeResultDescAdd", {challengeId});

      applyAddResultDescriptionMutation({
        variables: {data, challengeId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyAddResultDescriptionMutation, challenge],
  );

  const onResultDescUpdate = useCallback(
    (data: ResultDescriptionInput, resultDescriptionId: string) => {
      const challengeId = challenge.id;
      analyticsSendEvent("editorChallengeResultDescUpdate", {challengeId, resultDescriptionId});

      applyUpdateResultDescriptionMutation({
        variables: {data, challengeId, resultDescriptionId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyUpdateResultDescriptionMutation, challenge],
  );

  const onResultDescDelete = useCallback(
    (challengeId: string, resultDescriptionId: string) => {
      analyticsSendEvent("editorChallengeResultDescDelete", {challengeId, resultDescriptionId});
      applyDeleteResultDescriptionMutation({
        variables: {challengeId, resultDescriptionId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyDeleteResultDescriptionMutation],
  );

  const onAddResultDescButtonClick = useCallback(() => {
    const modal = addModal(
      {
        header: t("components.ResultDesc.editModalTitle"),
        content: (
          <ResultDescriptionModal.Content
            onSave={(data) => {
              onResultDescAdd && onResultDescAdd(data);
              modal.remove();
            }}
          />
        ),
      },
      true,
    );
  }, [addModal, t, onResultDescAdd]);

  const onUpdateResultDescButtonClick = useCallback(
    (e: React.MouseEvent) => {
      const descId = e.currentTarget.getAttribute("data-descid")!;
      const challengeId = challenge.id;
      const modal = addModal(
        {
          header: t("components.ResultDesc.editModalTitle"),
          content: (
            <ResultDescriptionModal.Content
              desc={resultDescriptions?.find((desc) => desc.id === descId)}
              onSave={(data) => {
                onResultDescUpdate && onResultDescUpdate(data, descId);
                modal.remove();
              }}
              onDelete={() => {
                onResultDescDelete && onResultDescDelete(challengeId, descId);
                modal.remove();
              }}
            />
          ),
        },
        true,
      );
    },
    [addModal, t, onResultDescUpdate, onResultDescDelete, challenge, resultDescriptions],
  );

  const [allResultDescsVisible, showAllResultDescs, showLessResultDescs] = useVisibility();

  const displayResultDescs = useMemo(() => {
    if (!resultDescriptions) {
      return;
    }
    if (!allResultDescsVisible && resultDescriptions.length > 3) {
      return resultDescriptions.slice(0, 3);
    }
    return resultDescriptions;
  }, [allResultDescsVisible, resultDescriptions]);

  const [applyAddParametricResultDescriptionMutation] = useAddParametricResultDescriptionMutation();
  const [applyUpdateParametricResultDescriptionMutation] = useUpdateParametricResultDescriptionMutation();
  const [applyDeleteParametricResultDescriptionMutation] = useDeleteParametricResultDescriptionMutation();

  const onParametricResultDescAdd = useCallback(
    (data: ParametricResultDescriptionInput) => {
      const challengeId = challenge.id;
      analyticsSendEvent("editorChallengeParametricResultDescAdd", {challengeId});

      applyAddParametricResultDescriptionMutation({
        variables: {data, challengeId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyAddParametricResultDescriptionMutation, challenge],
  );

  const onParametricResultDescUpdate = useCallback(
    (data: ParametricResultDescriptionInput, resultDescriptionId: string) => {
      const challengeId = challenge.id;
      analyticsSendEvent("editorChallengeParametricResultDescUpdate", {challengeId, resultDescriptionId});

      applyUpdateParametricResultDescriptionMutation({
        variables: {data, challengeId, resultDescriptionId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyUpdateParametricResultDescriptionMutation, challenge],
  );

  const onParametricResultDescDelete = useCallback(
    (challengeId: string, resultDescriptionId: string) => {
      analyticsSendEvent("editorChallengeParametricResultDescDelete", {challengeId, resultDescriptionId});
      applyDeleteParametricResultDescriptionMutation({
        variables: {challengeId, resultDescriptionId},
        refetchQueries: ["EditorEditChallengeSceneQuery"],
      });
    },
    [applyDeleteParametricResultDescriptionMutation],
  );

  const onAddParametricResultDescButtonClick = useCallback(() => {
    const isCompetenceTest = challenge.mode === ChallengeMode.COMPETENCE_TEST;
    const modal = addModal(
      {
        header: <ParametricResultDescriptionModal.Header />,
        content: (
          <ParametricResultDescriptionModal.Content
            parameters={isCompetenceTest ? undefined : challenge.customParameters}
            competencies={isCompetenceTest ? competencies : undefined}
            useCompetences={isCompetenceTest}
            singleCondition={challenge.showResultsForEachParameter}
            onSave={(data) => {
              onParametricResultDescAdd && onParametricResultDescAdd(data);
              modal.remove();
            }}
          />
        ),
      },
      true,
    );
  }, [
    addModal,
    onParametricResultDescAdd,
    challenge.customParameters,
    challenge.showResultsForEachParameter,
    challenge.mode,
    competencies,
  ]);

  const onUpdateParametricResultDescButtonClick = useCallback(
    (e: React.MouseEvent) => {
      const descId = e.currentTarget.getAttribute("data-descid")!;
      const challengeId = challenge.id;
      const isCompetenceTest = challenge.mode === ChallengeMode.COMPETENCE_TEST;
      const modal = addModal(
        {
          header: <ParametricResultDescriptionModal.Header />,
          content: (
            <ParametricResultDescriptionModal.Content
              desc={parametricResultDescriptions?.find((desc) => desc.id === descId)}
              parameters={isCompetenceTest ? undefined : challenge.customParameters}
              competencies={isCompetenceTest ? competencies : undefined}
              useCompetences={isCompetenceTest}
              singleCondition={challenge.showResultsForEachParameter}
              onSave={(data) => {
                onParametricResultDescUpdate && onParametricResultDescUpdate(data, descId);
                modal.remove();
              }}
              onDelete={() => {
                onParametricResultDescDelete && onParametricResultDescDelete(challengeId, descId);
                modal.remove();
              }}
            />
          ),
        },
        true,
      );
    },
    [
      addModal,
      onParametricResultDescUpdate,
      onParametricResultDescDelete,
      challenge,
      parametricResultDescriptions,
      competencies,
    ],
  );

  const [allParametricResultDescsVisible, showAllParametricResultDescs, showLessParametricResultDescs] =
    useVisibility();

  const displayParametricResultDescs = useMemo(() => {
    if (!parametricResultDescriptions) {
      return;
    }
    if (!allParametricResultDescsVisible && parametricResultDescriptions.length > 3) {
      return parametricResultDescriptions.slice(0, 3);
    }
    return parametricResultDescriptions;
  }, [allParametricResultDescsVisible, parametricResultDescriptions]);

  const updateCustomParameterIds = useCallback(
    (ids: string[]) => {
      formState.setValues({customParameterIds: ids ?? []});
    },
    [formState],
  );

  const updateCompetences = useCallback(
    (ids?: string[]) => {
      formState.setValues({competenceIds: ids ?? []});
    },
    [formState],
  );

  const allowDefaultFeatures = ![ChallengeMode.COMPETENCE_TEST, ChallengeMode.RPG_MODE].includes(challenge.mode);

  return (
    <form className={className} method="post" onSubmit={formState.submitHandler}>
      <div className={classes.formItem}>
        <label className={classes.label}>{t("components.ChallengeCustomizeForm.avatar")}</label>
        <AvatarSelect
          className={tourClasses.avatarSelect}
          name="avatarId"
          avatars={avatarsEx}
          value={formState.values.avatarId}
          onChange={disabled ? undefined : formState.changeHandler}
        />
      </div>

      <div className={cn(classes.formItem, tourClasses.challengeType)}>
        <label className={classes.label}>
          {t("components.ChallengeCustomizeForm.challengeType")}
          <Link className={classes.moreInfo} onClick={onChallengeTypeHelpClick}>
            {t("components.ChallengeCustomizeForm.more")}
          </Link>
        </label>
        <WithTooltip as="span" placement="left" className={classes.help} helpText={challengeModeHelpText}>
          <Select name="mode" value={formState.values.mode} disabled={disabled} onChange={formState.changeHandler}>
            <option value={ChallengeMode.COMPETENCE_TEST}>{t("types.ChallengeMode.COMPETENCE_TEST")}</option>
            <option value={ChallengeMode.CHOICE_BASED_STUDY}>{t("types.ChallengeMode.CHOICE_BASED_STUDY")}</option>
            <option value={ChallengeMode.CHOICES_WITH_CUSTOM_SCORES}>
              {t("types.ChallengeMode.CHOICES_WITH_CUSTOM_SCORES")}
            </option>
            <option value={ChallengeMode.CUSTOM_PARAMETERS_TEST}>
              {t("types.ChallengeMode.CUSTOM_PARAMETERS_TEST")}
            </option>
            <option value={ChallengeMode.RPG_MODE}>{t("types.ChallengeMode.RPG_MODE")}</option>
          </Select>
        </WithTooltip>
      </div>

      {challenge.mode === ChallengeMode.RPG_MODE && (
        <div className={classes.formItem}>
          <label className={classes.label}>{t("components.ChallengeCustomizeForm.instructionFile.label")}</label>
          {challenge.rpgInstructionTemplateDownloadUrl && (
            <p className={classes.instructionTemplateHint}>
              <a
                href={challenge.rpgInstructionTemplateDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download="rpg_instruction_template.txt"
              >
                {t("components.ChallengeCustomizeForm.rpgInstructionTemplateDownload")}
              </a>
              <WithTooltip
                as="span"
                className={classes.help}
                helpText={t("components.ChallengeCustomizeForm.instructionFile.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </p>
          )}

          <FileInput
            variant="any"
            src={instructionFileSrc}
            fileName={instructionFileName ?? undefined}
            name="instructionFile"
            onDelete={formState.changeHandler}
            setValues={formState.setValues}
            disabled={disabled}
            accept=".txt,.md,.markdown,text/plain,text/markdown"
          />
        </div>
      )}

      {challenge.mode === ChallengeMode.RPG_MODE && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.isUserStart.helpText")}
            >
              <Checkbox
                name="isUserStart"
                checked={formState.values.isUserStart === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>{t("components.ChallengeCustomizeForm.isUserStart.label")}</span>
            </WithTooltip>
          </label>
        </div>
      )}

      {(challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) && (
        <div className={classes.formItem}>
          <label className={classes.label}>
            {t("components.ChallengeCustomizeForm.competence.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.competence.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.competence.helpText")}
          >
            <CompetenciesCreatableSelect
              competencies={competencies ?? []}
              currentItemIds={currentCompetences}
              disabled={disabled}
              isMulti={true}
              onSelect={updateCompetences}
            />
          </WithTooltip>
          {competenciesFromActions.length > 0 && (
            <div className={classes.competenciesFromActions}>
              <span className={classes.competenciesFromActionsLabel}>
                {t("components.ChallengeCustomizeForm.competence.fromActions")}
              </span>
              <div className={classes.competenciesTagsContainer}>
                {competenciesFromActions.map((competence) => (
                  <div key={competence.id} className={classes.competenceTag}>
                    <span>{competence.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isStaff && (
        <>
          <div className={classes.formItem}>
            <label className={classes.label}>{t("components.ChallengeCustomizeForm.avatarVoice.label")}</label>
            <WithTooltip
              as="span"
              className={classes.help}
              helpText={t("components.ChallengeCustomizeForm.avatarVoice.helpText")}
            >
              <Select
                name="avatarVoiceId"
                value={formState.values.avatarVoiceId}
                disabled={disabled}
                onChange={formState.changeHandler}
              >
                {supportedVoices?.map((voice) => (
                  <option key={voice} value={voice}>
                    {voice}
                  </option>
                ))}
              </Select>
            </WithTooltip>
          </div>

          <div className={classes.formItem}>
            <label className={classes.label}>{t("components.ChallengeCustomizeForm.systemVoice.label")}</label>
            <WithTooltip
              as="span"
              className={classes.help}
              helpText={t("components.ChallengeCustomizeForm.systemVoice.helpText")}
            >
              <Select
                name="systemVoiceId"
                value={formState.values.systemVoiceId}
                disabled={disabled}
                onChange={formState.changeHandler}
              >
                {supportedVoices?.map((voice) => (
                  <option key={voice} value={voice}>
                    {voice}
                  </option>
                ))}
              </Select>
            </WithTooltip>
          </div>
        </>
      )}

      {challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && (
        <>
          <div className={classes.formItem}>
            <label className={classes.label} htmlFor="customParameters">
              {t("components.ChallengeCustomizeForm.customParameters.title")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.customParameters.help")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <CustomParametersEditor
              currentItemIds={formState.values.customParameterIds}
              onSelect={updateCustomParameterIds}
              disabled={disabled}
            />
          </div>

          <DisplayResultModeSelector
            name="displayParametricResultMode"
            value={formState.values.displayParametricResultMode}
            disabled={disabled}
            onChange={formState.changeHandler}
          />
        </>
      )}

      <div className={cn(classes.formItem, tourClasses.challengeInput)}>
        <label className={classes.label}>{t("components.ChallengeCustomizeForm.challengeInput")}</label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.ChallengeCustomizeForm.inputMode.helpText")}
        >
          <Select
            name="inputMode"
            value={formState.values.inputMode}
            disabled={disabled}
            onChange={formState.changeHandler}
          >
            <option value={ChallengeInputMode.VOICE}>{t("types.ChallengeInputMode.VOICE")}</option>
            <option value={ChallengeInputMode.TEXT}>{t("types.ChallengeInputMode.TEXT")}</option>
          </Select>
        </WithTooltip>
      </div>
      {inExperimentalMode && challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && (
        <div className={cn(classes.formItem)}>
          <label className={classes.label}>
            {t("components.ChallengeCustomizeForm.multipleChoiceScoringOption.label")}
          </label>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.multipleChoiceScoringOption.helpText")}
          >
            <Select
              name="multipleChoiceScoringOption"
              value={formState.values.multipleChoiceScoringOption}
              disabled={disabled}
              onChange={formState.changeHandler}
            >
              <option value={MultipleChoiceScoringOption.COMPLETELY_CORRECT}>
                {t("components.ChallengeCustomizeForm.multipleChoiceScoringOption.completelyCorrect")}
              </option>
              <option value={MultipleChoiceScoringOption.PARTLY_WITH_ERRORS}>
                {t("components.ChallengeCustomizeForm.multipleChoiceScoringOption.partlyWithErrors")}
              </option>
            </Select>
          </WithTooltip>
        </div>
      )}

      <div className={cn(classes.formItem, tourClasses.language)}>
        <label className={classes.label}>{t("components.ChallengeCustomizeForm.lang")}</label>
        <Select
          name="languageCode"
          className={classes.langSelector}
          value={formState.values.languageCode}
          disabled={disabled}
          onChange={formState.changeHandler}
        >
          {getLanguages().map((lang) => (
            <option key={lang.lang} value={lang.languageCode}>
              {lang.verboseName}
            </option>
          ))}
        </Select>
      </div>

      {allowDefaultFeatures && (
        <div className={cn(classes.formItem, tourClasses.recognitionAccuracy)}>
          <label className={classes.label}>{t("components.ChallengeCustomizeForm.recognitionAccuracy.label")}</label>
          <Select
            name="recognitionAccuracy"
            value={formState.values.recognitionAccuracy}
            disabled={disabled}
            onChange={formState.changeHandler}
          >
            <option value={ChallengeRecognitionAccuracy.WEAK}>
              {t("components.ChallengeCustomizeForm.recognitionAccuracy.weak")}
            </option>
            <option value={ChallengeRecognitionAccuracy.NORMAL}>
              {t("components.ChallengeCustomizeForm.recognitionAccuracy.normal")}
            </option>
            <option value={ChallengeRecognitionAccuracy.STRONG}>
              {t("components.ChallengeCustomizeForm.recognitionAccuracy.strong")}
            </option>
          </Select>
        </div>
      )}

      <div className={cn(classes.formItem, tourClasses.badWordsFilter)}>
        <label className={classes.label}>
          {t("components.ChallengeCustomizeForm.badWordsFilter.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.badWordsFilter.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.ChallengeCustomizeForm.badWordsFilter.helpText")}
        >
          <Select
            name="badWordsFilter"
            value={formState.values.badWordsFilter ?? undefined}
            disabled={disabled}
            onChange={formState.changeHandler}
          >
            <option value={ChallengeBadWordsFilter.NO}>
              {t("components.ChallengeCustomizeForm.badWordsFilter.no")}
            </option>
            <option value={ChallengeBadWordsFilter.FAIL}>
              {t("components.ChallengeCustomizeForm.badWordsFilter.fail")}
            </option>
            <option value={ChallengeBadWordsFilter.INTERRUPT}>
              {t("components.ChallengeCustomizeForm.badWordsFilter.interrupt")}
            </option>
          </Select>
        </WithTooltip>
      </div>

      <ChallengeDictionaryForm
        dictionaryType="trigger"
        triggerDicts={triggerDicts}
        challengeId={challenge.id}
        disabled={disabled}
      />

      {inExperimentalMode && challenge.mode !== ChallengeMode.COMPETENCE_TEST && (
        <ChallengeDictionaryForm
          dictionaryType="cutWords"
          cutWordsDicts={cutWordsDicts}
          challengeId={challenge.id}
          disabled={disabled}
        />
      )}

      {inExperimentalMode && (
        <ChallengeDictionaryForm
          dictionaryType="hotWords"
          hotWordsDicts={hotWordsDicts}
          challengeId={challenge.id}
          disabled={disabled}
        />
      )}

      {challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES && (
        <ResultDescriptionsList
          labelKey={t("components.ChallengeCustomizeForm.resultDescriptions.label")}
          helpTextKey={t("components.ChallengeCustomizeForm.resultDescriptions.helpText")}
          descriptions={resultDescriptions}
          displayDescriptions={displayResultDescs}
          allVisible={allResultDescsVisible}
          onShowAll={showAllResultDescs}
          onShowLess={showLessResultDescs}
          onAdd={onAddResultDescButtonClick}
          onUpdate={onUpdateResultDescButtonClick}
          formatDescription={(desc) => `${desc.minScore}% - ${desc.maxScore}%`}
          disabled={disabled}
          buttonClassName={classes.buttonBiggerText}
        />
      )}

      {challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && (
        <ResultDescriptionsList
          labelKey={t("components.ChallengeCustomizeForm.parametricResultDescriptions.label")}
          helpTextKey={t("components.ChallengeCustomizeForm.parametricResultDescriptions.helpText")}
          descriptions={parametricResultDescriptions}
          displayDescriptions={displayParametricResultDescs}
          allVisible={allParametricResultDescsVisible}
          onShowAll={showAllParametricResultDescs}
          onShowLess={showLessParametricResultDescs}
          onAdd={onAddParametricResultDescButtonClick}
          onUpdate={onUpdateParametricResultDescButtonClick}
          formatDescription={(desc) =>
            `${desc.message?.slice(0, 25)}${desc.message && desc.message?.length > 25 ? "..." : ""}`
          }
          disabled={disabled}
        />
      )}

      {challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <ResultDescriptionsList
          labelKey={t("components.ChallengeCustomizeForm.competenceResultDescriptions.label")}
          helpTextKey={t("components.ChallengeCustomizeForm.competenceResultDescriptions.helpText")}
          descriptions={parametricResultDescriptions}
          displayDescriptions={displayParametricResultDescs}
          allVisible={allParametricResultDescsVisible}
          onShowAll={showAllParametricResultDescs}
          onShowLess={showLessParametricResultDescs}
          onAdd={onAddParametricResultDescButtonClick}
          onUpdate={onUpdateParametricResultDescButtonClick}
          formatDescription={(desc) =>
            `${desc.message?.slice(0, 25)}${desc.message && desc.message?.length > 25 ? "..." : ""}`
          }
          disabled={disabled}
        />
      )}

      <div className={cn(classes.formItem, tourClasses.detailedResults)}>
        <label className={cn(classes.label, classes.checkboxLabel)}>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.showDetailedResults.helpText")}
          >
            <Checkbox
              name="showDetailedResults"
              checked={formState.values.showDetailedResults === "on"}
              onChange={formState.changeHandler}
              disabled={disabled}
            />
            <span className={classes.checkboxText}>
              {t("components.ChallengeCustomizeForm.showDetailedResults.label")}
            </span>
          </WithTooltip>
        </label>
      </div>

      {allowDefaultFeatures && (
        <div className={cn(classes.formItem, tourClasses.evaluateScenarios)}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.evaluateScenarios.helpText")}
            >
              <Checkbox
                name="evaluateScenarios"
                checked={formState.values.evaluateScenarios === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.evaluateScenarios.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      {allowDefaultFeatures && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.selectableInputs.helpText")}
            >
              <Checkbox
                name="selectableInputs"
                checked={formState.values.selectableInputs === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.selectableInputs.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      {inExperimentalMode && challenge.mode !== ChallengeMode.RPG_MODE && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.alwaysCanSkipSteps.helpText")}
            >
              <Checkbox
                name="alwaysCanSkipSteps"
                checked={formState.values.alwaysCanSkipSteps === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.alwaysCanSkipSteps.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      {challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.ignoreFailedInputs.helpText")}
            >
              <Checkbox
                name="ignoreFailedInputs"
                checked={formState.values.ignoreFailedInputs === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.ignoreFailedInputs.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      <div className={cn(classes.formItem, tourClasses.characterErrors)}>
        <label className={cn(classes.label, classes.checkboxLabel)}>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.useCharacterVoiceForErrors.helpText")}
          >
            <Checkbox
              name="useCharacterVoiceForErrors"
              checked={formState.values.useCharacterVoiceForErrors === "on"}
              onChange={formState.changeHandler}
              disabled={disabled}
            />
            <span className={classes.checkboxText}>
              {t("components.ChallengeCustomizeForm.useCharacterVoiceForErrors.label")}
            </span>
          </WithTooltip>
        </label>
      </div>

      <div className={cn(classes.formItem, tourClasses.muteVoices)}>
        <label className={cn(classes.label, classes.checkboxLabel)}>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.muteNonUserSpeech.helpText")}
          >
            <Checkbox
              name="muteNonUserSpeech"
              checked={formState.values.muteNonUserSpeech === "on"}
              onChange={formState.changeHandler}
              disabled={disabled}
            />
            <span className={classes.checkboxText}>
              {t("components.ChallengeCustomizeForm.muteNonUserSpeech.label")}
            </span>
          </WithTooltip>
        </label>
      </div>

      <div className={cn(classes.formItem, tourClasses.showPreviousPhrase)}>
        <label className={cn(classes.label, classes.checkboxLabel)}>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.showPreviousPhrase.helpText")}
          >
            <Checkbox
              name="showPreviousPhrase"
              checked={formState.values.showPreviousPhrase === "on"}
              onChange={formState.changeHandler}
              disabled={disabled}
            />
            <span className={classes.checkboxText}>
              {t("components.ChallengeCustomizeForm.showPreviousPhrase.label")}
            </span>
          </WithTooltip>
        </label>
      </div>

      {isStaff && challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.showResultsForEachParameter.helpText")}
            >
              <Checkbox
                name="showResultsForEachParameter"
                checked={formState.values.showResultsForEachParameter === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.showResultsForEachParameter.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}
      {isStaff && challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.showResultsForEachCompetence.helpText")}
            >
              <Checkbox
                name="showResultsForEachParameter"
                checked={formState.values.showResultsForEachParameter === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.showResultsForEachCompetence.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      {challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.disableAllResponses.helpText")}
            >
              <Checkbox
                name="isAllResponsesEnabled"
                checked={formState.values.isAllResponsesEnabled === "on"}
                onChange={formState.changeHandler}
                disabled={disabled}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.disableAllResponses.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      {(challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) && (
        <div className={classes.formItem}>
          <label className={cn(classes.label, classes.checkboxLabel)}>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={
                currentCompetences.length === 0 && competenciesFromActions.length === 0
                  ? t("components.ChallengeCustomizeForm.generativeFeedback.requireCompetence")
                  : t("components.ChallengeCustomizeForm.generativeFeedback.helpText")
              }
            >
              <Checkbox
                name="isSessionResultFeedbackEnabled"
                checked={formState.values.isSessionResultFeedbackEnabled === "on"}
                onChange={(e) => {
                  const hasCompetences = currentCompetences.length + competenciesFromActions.length > 0;
                  const requireCompetence = t(
                    "components.ChallengeCustomizeForm.generativeFeedback.requireCompetence",
                  );

                  if (e.target.checked && !hasCompetences) {
                    toast.error(requireCompetence, {
                      position: "bottom-right",
                      autoClose: 4000,
                    });
                    return;
                  }

                  formState.changeHandler(e);
                }}
              />
              <span className={classes.checkboxText}>
                {t("components.ChallengeCustomizeForm.generativeFeedback.label")}
              </span>
            </WithTooltip>
          </label>
        </div>
      )}

      <div className={cn(classes.formItem, tourClasses.background)}>
        <label className={classes.label}>{t("components.ChallengeCustomizeForm.background")}</label>
        <FileInput
          variant="image"
          src={imageSrc}
          name="background"
          onDelete={formState.changeHandler}
          setValues={formState.setValues}
          disabled={disabled}
        />
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.ChallengeCustomizeForm.startMessage.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.startMessage.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <EditableTextarea.Wrapper multiline className={classes.nameWrapper}>
          <EditableTextarea
            maxLength={400}
            id="startMessage"
            name="startMessage"
            rows={4}
            defaultValue={formState.values.startMessage ?? undefined}
            onChange={formState.changeHandler}
            placeholder={t("components.ChallengeCustomizeForm.startMessage.placeholder")}
            buttonClassName={classes.inputButtons}
            disabled={disabled}
          />
        </EditableTextarea.Wrapper>
      </div>

      {challenge.mode !== ChallengeMode.CHOICES_WITH_CUSTOM_SCORES && (
        <div className={classes.formItem}>
          <label className={classes.label}>{t("components.ChallengeCustomizeForm.resultMessage.label")}</label>
          <EditableTextarea.Wrapper multiline className={classes.nameWrapper}>
            <EditableTextarea
              maxLength={1500}
              id="resultMessage"
              name="resultMessage"
              rows={4}
              defaultValue={formState.values.resultMessage ?? undefined}
              onChange={formState.changeHandler}
              placeholder={t("components.ChallengeCustomizeForm.resultMessage.placeholder")}
              buttonClassName={classes.inputButtons}
              disabled={disabled}
            />
          </EditableTextarea.Wrapper>
        </div>
      )}

      {inExperimentalMode && (
        <>
          <div className={classes.formItem}>
            <label className={classes.label}>
              {t("components.ChallengeCustomizeForm.attemptsLimit.label")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.attemptsLimit.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.attemptsLimit.helpText")}
            >
              <Input
                style={{width: "100px", height: "40px"}}
                required
                min={0}
                type="number"
                name="attemptsLimit"
                value={formState.values.attemptsLimit}
                onChange={formState.changeHandler}
              />
            </WithTooltip>
          </div>
          <div className={classes.formItem}>
            <label className={classes.label}>
              {t("components.ChallengeCustomizeForm.timeLimit.label")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.timeLimit.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.timeLimit.helpText")}
            >
              <Input
                style={{width: "100px", height: "40px"}}
                required
                min={0}
                type="number"
                name="timeLimit"
                value={formState.values.timeLimit}
                onChange={formState.changeHandler}
              />
            </WithTooltip>
          </div>
        </>
      )}

      <div className={classes.formItem}>
          <label className={classes.label}>
            {t("components.ChallengeCustomizeForm.actionTimeLimit.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.actionTimeLimit.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ChallengeCustomizeForm.actionTimeLimit.helpText")}
          >
            <Input
              style={{width: "100px", height: "40px"}}
              required
              min={0}
              type="number"
              name="actionTimeLimit"
              value={formState.values.actionTimeLimit}
              onChange={formState.changeHandler}
            />
          </WithTooltip>
      </div>


      {inExperimentalMode && (
        <>
          <div className={classes.formItem}>
            <label className={classes.label}>
              {t("components.ChallengeCustomizeForm.inputPhraseMaxLength.label")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.inputPhraseMaxLength.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.inputPhraseMaxLength.helpText")}
            >
              <Input
                style={{width: "100px", height: "40px"}}
                required
                min={0}
                type="number"
                name="inputPhraseMaxLength"
                value={formState.values.inputPhraseMaxLength}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    formState.setValues({inputPhraseMaxLength: undefined});
                    return;
                  }
                  formState.changeHandler(e);
                }}
              />
            </WithTooltip>
          </div>
          <div className={classes.formItem}>
            <label className={classes.label}>
              {t("components.ChallengeCustomizeForm.avatarPhraseMaxLength.label")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.avatarPhraseMaxLength.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.avatarPhraseMaxLength.helpText")}
            >
              <Input
                style={{width: "100px", height: "40px"}}
                required
                min={0}
                max={YANDEX_TTS_PHRASE_MAX_LENGTH}
                type="number"
                name="avatarPhraseMaxLength"
                value={formState.values.avatarPhraseMaxLength}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    formState.setValues({avatarPhraseMaxLength: undefined});
                    return;
                  }
                  formState.changeHandler(e);
                }}
              />
            </WithTooltip>
          </div>
          <div className={classes.formItem}>
            <label className={classes.label}>
              {t("components.ChallengeCustomizeForm.systemPhraseMaxLength.label")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ChallengeCustomizeForm.systemPhraseMaxLength.helpText")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </label>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChallengeCustomizeForm.systemPhraseMaxLength.helpText")}
            >
              <Input
                style={{width: "100px", height: "40px"}}
                required
                min={0}
                max={YANDEX_TTS_PHRASE_MAX_LENGTH}
                type="number"
                name="systemPhraseMaxLength"
                value={formState.values.systemPhraseMaxLength}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    formState.setValues({systemPhraseMaxLength: undefined});
                    return;
                  }
                  formState.changeHandler(e);
                }}
              />
            </WithTooltip>
          </div>
        </>
      )}
    </form>
  );
}

ChallengeCustomizeForm.fragments = {
  root: gql`
    fragment ChallengeCustomizeForm on Challenge {
      id
      languageCode
      recognitionAccuracy
      recognitionEngine
      mode
      displayMode
      inputMode
      generateVideo
      showDetailedResults
      evaluateScenarios
      selectableInputs
      alwaysCanSkipSteps
      ignoreFailedInputs
      showResultsForEachParameter
      inviteByLink
      useCharacterVoiceForErrors
      muteNonUserSpeech
      showPreviousPhrase
      startMessage
      resultMessage
      attemptsLimit
      timeLimit
      actionTimeLimit
      inputPhraseMaxLength
      avatarPhraseMaxLength
      systemPhraseMaxLength
      customParameters {
        id
        name
      }
      avatarVoiceId
      systemVoiceId
      displayParametricResultMode
      isAllResponsesEnabled
      isSessionResultFeedbackEnabled
      multipleChoiceScoringOption
      instructionFile
      instructionFileName
      rpgInstructionParseStatus
      isAgentStart
      rpgInstructionTemplateDownloadUrl
      competences {
        id
        name
        __typename
      }
      avatar {
        id
        ...AvatarSelect
      }
    }

    ${AvatarSelect.fragments.root}
  `,

  avatars: gql`
    fragment ChallengeCustomizeFormAvatars on Avatar {
      ...AvatarSelect
    }
    ${AvatarSelect.fragments.root}
  `,

  competencies: gql`
    fragment ChallengeCustomizeFormCompetencies on Competence {
      id
      name
      description
      indicators
    }
  `,
};
